// src/app/modules/shared/services/hunt-package.service.ts
import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable } from 'rxjs'

import {
  Enums,
  HuntPackageRecord,
  HuntSummary,
  Profile,
} from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../../../environments/verity-environment.service'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../tracking/tracking.service'

@Injectable({ providedIn: 'root' })
export class HuntPackageService {
  private httpClient = inject(HttpClient)
  private trackingService = inject(TrackingService)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  /** Fetch a single package record */
  getHuntPackageByUUID(uuid: string): Observable<HuntPackageRecord> {
    return this.httpClient.get<HuntPackageRecord>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/${uuid}`,
    )
  }
  /** Fetch multiple packages by UUID */
  getHuntPackagesByUUIDs(uuids: string[]): Observable<HuntPackageRecord[]> {
    return this.httpClient.get<HuntPackageRecord[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/list/`,
      { params: { uuids } },
    )
  }
  /** Fetch all packages */

  /** Related hunts for a given package */
  getRelatedHunts(uuid: string): Observable<HuntSummary[]> {
    return this.httpClient.get<HuntSummary[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/${uuid}/related-hunts`,
    )
  }

  /** Download‐ready blob endpoint for validation ZIP */
  getHuntPackageValidationBlob(uuid: string): Observable<Blob> {
    return this.httpClient.get(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/${uuid}/validation`,
      { responseType: 'blob' },
    )
  }

  /**
   * Fire-and-forget download of the validation ZIP (plus analytics).
   */
  downloadHuntPackageValidation(uuid: string, filename: string): void {
    this.getHuntPackageValidationBlob(uuid).subscribe({
      next: (data: Blob) => {
        const tempUrl = window.URL.createObjectURL(data)
        const a = document.createElement('a')
        a.href = tempUrl
        a.download = `${filename} - AtomicValidation.zip`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(tempUrl)
      },
      error: (err) => {
        console.error('Validation download failed', err)
      },
    })

    this.trackingService.trackEvent(
      TrackingEventEnum.RESEARCH_VALIDATION_PACKAGE_DOWNLOADED,
      { UUID: uuid, filename },
    )
  }

  /** Search by threat-profile terms */
  getRelatedHuntPackages(terms: string[]): Observable<HuntPackageRecord[]> {
    return this.httpClient.get<HuntPackageRecord[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/threat-profile-query`,
      { params: { term: terms.join(',') } },
    )
  }

  /** Popular packages (top 3) */
  getPopularHuntPackages(): Observable<HuntPackageRecord[]> {
    return this.httpClient.get<HuntPackageRecord[]>(
      `${this.verityEnvironmentService.apiUrl}/es/popular/${Enums.CyborgIndexEnum.CYBORG_USE_CASES}?size=3`,
    )
  }

  /** Recently-updated packages */
  getRecentHuntPackages(size: number): Observable<HuntPackageRecord[]> {
    return this.httpClient.get<HuntPackageRecord[]>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/summary/recently-updated?size=${size}`,
    )
  }

  /** MITRE aggregations summary */
  getMITREAggregations(): Observable<Record<string, unknown>> {
    return this.httpClient.get<Record<string, unknown>>(
      `${this.verityEnvironmentService.apiUrl}/es/cyborg-hunt-package/summary/mitre-aggregation`,
    )
  }

  /** Trial-access logic */
  huntPackageIsAccessibleForUser(
    huntPackage: HuntPackageRecord,
    user: Profile,
  ): boolean {
    const inTrial = user.company.status === 'trial'
    if (!inTrial) return true

    const allowed = (user.company.trialAccessibleHuntPackages || []).map((u) =>
      u.toLowerCase(),
    )
    return (
      huntPackage.community || allowed.includes(huntPackage.UUID.toLowerCase())
    )
  }
}
